import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

export type Config = {
    onLoad?: () => void;
    goBackOnSubmit?: number[];
    removeOnSubmit?: number[];
};

export type SubmitHandler = (value: number, metaData: Record<string, string>, config?: Config) => void;

function useNPS(onSubmit: SubmitHandler) {
    const navigate = useNavigate();
    const configRef = useRef<Config>();

    useEffect(() => {
        const handleNpsEvents = (msg: MessageEvent) => {
            const npsForm = document.getElementById('nps_form') as HTMLIFrameElement;
            if (!npsForm || msg.data?.type !== 'nps_event') return;

            switch (msg.data?.action) {
                case 'close': {
                    if (msg.data?.value) npsForm.remove();
                    break;
                }

                case 'mount': {
                    if (!msg.data?.value) break;
                    npsForm.classList.remove('hidden');
                    npsForm.contentWindow?.postMessage({ type: 'nps_event', action: 'open', value: true }, '*');
                    break;
                }

                case 'submit': {
                    const value = Number(msg.data.value) || 0;
                    const metaData: Record<string, string> = {};

                    msg.data.metaData?.forEach((item: { attribute: string; value: string }) => {
                        metaData[item.attribute] = item.value;
                    });

                    onSubmit(value, metaData, configRef.current);

                    if (!configRef.current) return;

                    const { goBackOnSubmit, removeOnSubmit } = configRef.current;

                    if (goBackOnSubmit?.includes(value)) setTimeout(() => navigate(-1), 2000);
                    if (removeOnSubmit?.includes(value)) npsForm.remove();
                    break;
                }

                default:
                    break;
            }
        };

        window.addEventListener('message', handleNpsEvents);
        return () => window.removeEventListener('message', handleNpsEvents);
    }, []);

    const loadNPS = (npsURL: string, config?: Config) => {
        if (!npsURL) return;

        configRef.current = config;

        const iframe = document.createElement('iframe');
        iframe.id = 'nps_form';
        iframe.className = 'hidden fixed left-0 top-0 w-full h-full z-50';
        iframe.src = npsURL;

        config?.onLoad?.();

        const npsForm = document.getElementById('nps_form');

        if (npsForm) {
            npsForm.replaceWith(iframe);
            return;
        }

        document.body.appendChild(iframe);
    };

    return { loadNPS };
}

export default useNPS;
